import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Send, CheckCircle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { WEST_VALLEY_CITIES, PLUMBING_SERVICES, PURIFICATION_SERVICES, EMAIL, BUSINESS_NAME_LEGAL, PHONE } from "@/lib/constants";

// PLACEHOLDER: Replace with your serverless endpoint URL when ready
const FORM_ENDPOINT = ""; // e.g., "https://api.yourdomain.com/contact"

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    city: "",
    message: "",
    smsConsent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const allServices = [...PLUMBING_SERVICES, ...PURIFICATION_SERVICES];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (FORM_ENDPOINT) {
      // Preferred: POST to serverless endpoint
      try {
        await fetch(FORM_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });
        setIsSubmitted(true);
      } catch (error) {
        // Fallback to mailto
        window.location.href = `mailto:${EMAIL}?subject=Service Request from ${formData.name}&body=${encodeURIComponent(
          `Name: ${formData.name}\nPhone: ${formData.phone}\nEmail: ${formData.email}\nService: ${formData.service}\nCity: ${formData.city}\nMessage: ${formData.message}`
        )}`;
      }
    } else {
      // Fallback: mailto link
      window.location.href = `mailto:${EMAIL}?subject=Service Request from ${formData.name}&body=${encodeURIComponent(
        `Name: ${formData.name}\nPhone: ${formData.phone}\nEmail: ${formData.email}\nService: ${formData.service}\nCity: ${formData.city}\nMessage: ${formData.message}`
      )}`;
      setIsSubmitted(true);
    }

    setIsSubmitting(false);
  };

  if (isSubmitted) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 text-center animate-fade-in">
        <CheckCircle className="h-16 w-16 text-primary mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-foreground mb-2">
          Request Received!
        </h3>
        <p className="text-muted-foreground">
          Thank you for contacting us. We'll get back to you as soon as possible.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-card border border-border rounded-xl p-6 md:p-8 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Full Name *</Label>
          <Input
            id="name"
            required
            placeholder="John Smith"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="bg-muted border-border"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Phone Number *</Label>
          <Input
            id="phone"
            type="tel"
            required
            placeholder="(480) 555-1234"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="bg-muted border-border"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email Address</Label>
        <Input
          id="email"
          type="email"
          placeholder="john@example.com"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="bg-muted border-border"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="service">Service Needed *</Label>
          <Select
            required
            value={formData.service}
            onValueChange={(value) => setFormData({ ...formData, service: value })}
          >
            <SelectTrigger id="service" aria-label="Service needed" className="bg-muted border-border">
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="emergency">Emergency Plumbing</SelectItem>
              {allServices.map((service) => (
                <SelectItem key={service.slug} value={service.slug}>
                  {service.title}
                </SelectItem>
              ))}
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="city">Your City *</Label>
          <Select
            required
            value={formData.city}
            onValueChange={(value) => setFormData({ ...formData, city: value })}
          >
            <SelectTrigger id="city" aria-label="Your city" className="bg-muted border-border">
              <SelectValue placeholder="Select your city" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              {WEST_VALLEY_CITIES.map((city) => (
                <SelectItem key={city} value={city}>
                  {city}, AZ
                </SelectItem>
              ))}
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="message">Tell Us About Your Issue</Label>
        <Textarea
          id="message"
          placeholder="Describe your plumbing issue or what service you need..."
          rows={4}
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          className="bg-muted border-border resize-none"
        />
      </div>

      {/* SMS Consent Checkbox - Required for A2P Compliance */}
      <div className="flex items-start gap-3">
        <Checkbox
          id="smsConsent"
          checked={formData.smsConsent}
          onCheckedChange={(checked) =>
            setFormData({ ...formData, smsConsent: checked === true })
          }
          className="mt-1"
          required
        />
        <Label htmlFor="smsConsent" className="text-sm text-foreground/80 leading-relaxed cursor-pointer">
          I consent to receive SMS notifications and alerts from {BUSINESS_NAME_LEGAL}.
          Message frequency varies. Msg &amp; data rates may apply. Text HELP to{" "}
          {PHONE} for help. Reply STOP to unsubscribe. View our{" "}
          <Link to="/privacy-policy/" className="text-primary hover:text-accent underline">
            Privacy Policy
          </Link>{" "}
          and{" "}
          <Link to="/terms-of-service/" className="text-primary hover:text-accent underline">
            Terms of Service
          </Link>.
        </Label>
      </div>

      <Button
        type="submit"
        variant="hero"
        size="xl"
        className="w-full"
        disabled={isSubmitting || !formData.smsConsent}
      >
        {isSubmitting ? (
          "Sending..."
        ) : (
          <>
            <Send className="h-5 w-5" />
            Request Service
          </>
        )}
      </Button>
    </form>
  );
};

export default ContactForm;
